"use client";

// [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001]
// 플로팅 위젯(채팅/메모) 위치 프리셋 선택 + 초기화 메뉴. 채팅/메모 공용.
// localStorage 기반(기기/브라우저별). DB 저장 없음.

import { useEffect, useRef, useState } from "react";
import {
  FLOATING_POSITION_OPTIONS,
  type FloatingPosition,
} from "@/lib/floatingPosition";

interface FloatingPositionMenuProps {
  current: FloatingPosition;
  onSelect: (pos: FloatingPosition) => void;
  onReset: () => void;
  /** 버튼 색상 톤 — 채팅(blue)/메모(amber) 구분용. */
  tone?: "blue" | "amber";
}

export default function FloatingPositionMenu({
  current,
  onSelect,
  onReset,
  tone = "blue",
}: FloatingPositionMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // 바깥 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const accent = tone === "amber" ? "text-amber-700" : "text-blue-700";
  const activeBg = tone === "amber" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800";

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-label="위치 변경"
        title="위치 변경"
        onClick={() => setOpen((v) => !v)}
        className="text-gray-500 hover:text-gray-800 px-1.5 py-0.5"
      >
        ⚙️
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[200]"
          role="menu"
        >
          <div className={`px-2 py-1 text-[10px] font-bold ${accent}`}>위치 선택</div>
          {FLOATING_POSITION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="menuitem"
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
              className={`w-full text-left text-xs px-3 py-1.5 hover:bg-gray-50 ${
                current === opt.value ? activeBg : "text-gray-700"
              }`}
            >
              {opt.label}
              {current === opt.value && <span className="ml-1">✓</span>}
            </button>
          ))}
          <div className="border-t my-1" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onReset();
              setOpen(false);
            }}
            className="w-full text-left text-xs px-3 py-1.5 text-gray-500 hover:bg-gray-50"
          >
            ↺ 기본 위치로 초기화
          </button>
        </div>
      )}
    </div>
  );
}
